import { FeatureGate } from '@orn/global/featureGates/featureGates.js';
import logger from '@orn/shared-web/lib/logger';
import * as React from 'react';
import { useNavigate } from 'react-router-dom';

import { useAlertBlockedNotice } from '../../hooks/assistant/useAlertBlockedNotice';
import { useMarkWorkItemsRead } from '../../hooks/mutations/useMarkWorkItemsRead';
import { useUpdateWorkItem } from '../../hooks/mutations/useUpdateWorkItem';
import {
  UNARCHIVED_WORK_ITEM_FILTERS,
  useWorkItems,
  WORK_ITEM_ALERTS_PAGE_SIZE,
} from '../../hooks/queries/useWorkItems';
import { useWorkItemStatusCounts } from '../../hooks/queries/useWorkItemStatusCounts';
import { useFeatureGate } from '../../hooks/useFeatureGates';
import { useWorkItemNeedAttentionFloor } from '../../hooks/useWorkItemNeedAttentionFloor';
import { PATHS } from '../../paths';
import { useAssistantTurnStatus } from '../../providers/AssistantTurnStatusProvider';
import { groupWorkItemsBySection } from '../../utils/alerts';
import { getWorkItemAlertRowAction } from '../../utils/workItemAlertAction';
import { isWorkItemUnread, workItemToListedAlert } from '../../utils/workItemAlertRow';
import { useAlertRowAction } from '../alerts/useAlertRowAction';

import AssistantAlertCarouselBase from './AssistantAlertCarouselBase';

import type { AssistantAlert } from './AssistantAlertCarouselBase';
import type { WorkItemDetailsFragment } from '../alerts/__generated__/workItemFragment.gql';

/** Module-level so the default prop is referentially stable across renders. */
const EMPTY_EXPANDED_IDS: readonly string[] = [];

export interface IAssistantAlertCarouselProps {
  /**
   * Open the clicked alert in the assistant transcript. Supplied by the pane,
   * which owns the WebSocket — the strip only decides *which* rows expand.
   * Omitted, a click still marks the row read and selects it.
   */
  onExpandAlert?: (alertId: string, wasUnread: boolean) => void;
  /**
   * Alerts the transcript already holds a card for. This is what decides whether
   * a click opens a turn — see {@link handleSelect}. Defaults to none, so a
   * strip rendered without it expands on every click.
   */
  expandedAlertIds?: readonly string[];
  className?: string;
}

/**
 * The assistant composer's alert strip, wired to `work_items` and rendering
 * {@link AssistantAlertCarouselBase}.
 *
 * One read, split client-side: `getWorkItems` deliberately offers no status
 * filter (status is derived per viewer and clock), so the strip takes the
 * unarchived set — the Center's own live read, same variables, one cache
 * entry — and keeps only the rows whose server-derived status is ACTIVE. The
 * strip is the needs-attention surface; a PENDING row waits on someone else
 * and lives in the Alerts Center instead.
 *
 * The expand seam stays id-keyed: a row click reports the work item's id
 * through `onExpandAlert`, and the `alert_expand` path resolves it against the
 * work-item store server-side and streams the row's stored summary as the
 * reply — no callout twin is involved.
 */
const AssistantAlertCarousel = ({
  onExpandAlert,
  expandedAlertIds = EMPTY_EXPANDED_IDS,
  className,
}: IAssistantAlertCarouselProps) => {
  const isAlertsCenterEnabled = useFeatureGate(FeatureGate.AlertsCenter);
  const navigate = useNavigate();
  const { markRead } = useMarkWorkItemsRead();
  const { archiveWorkItem } = useUpdateWorkItem();
  const { runAction, alertActionSlot } = useAlertRowAction();
  const [selectedId, setSelectedId] = React.useState<string | null>(null);
  const { isTurnInFlight } = useAssistantTurnStatus();
  const { blockedNoticeAlertId, showBlockedNotice } = useAlertBlockedNotice(isTurnInFlight);

  const skip = !isAlertsCenterEnabled;
  const live = useWorkItems({
    filters: UNARCHIVED_WORK_ITEM_FILTERS,
    pageSize: WORK_ITEM_ALERTS_PAGE_SIZE,
    skip,
  });
  const { counts, loading: countsLoading } = useWorkItemStatusCounts({ skip });

  // A failed read renders as an absent strip rather than an error banner — this
  // sits above the composer, where a raw GraphQL message would cost the user
  // more than the missing alerts do. Logged so the failure is still diagnosable.
  React.useEffect(() => {
    if (live.error) {
      logger.error('Failed to load assistant work-item alerts:', live.error);
    }
  }, [live.error]);

  const needAttention = React.useMemo(() => groupWorkItemsBySection(live.workItems).needAttention, [live.workItems]);

  useWorkItemNeedAttentionFloor({
    activeCount: counts.active,
    needAttentionCount: needAttention.length,
    countsLoading,
    live,
  });

  const byId = React.useMemo(() => {
    const map = new Map<string, WorkItemDetailsFragment>();
    for (const workItem of needAttention) {
      map.set(workItem.id, workItem);
    }
    return map;
  }, [needAttention]);

  const handleSelect = React.useCallback(
    (alert: AssistantAlert) => {
      // Mid-turn, every row is locked — including ones whose click would only
      // navigate, because the user cannot tell those apart and a user-launched
      // Plaid flow could land on top of one the running turn opens. The click
      // is refused whole, so the row is not marked read either.
      if (isTurnInFlight) {
        showBlockedNotice(alert.id);
        return;
      }

      void markRead([alert.id]);

      // An alert the user can act on sends them at the fix instead of into the
      // transcript, so it never takes the selected border.
      const workItem = byId.get(alert.id);
      if (workItem && runAction(getWorkItemAlertRowAction(workItem))) {
        return;
      }

      // Re-clicking a row the transcript already has a card for is a no-op
      // rather than a second turn — the guard is keyed on the transcript, not
      // the border, because the border is cleared by a re-render.
      if (!expandedAlertIds.includes(alert.id)) {
        // Read off the item rather than the row: `markRead` above has not
        // landed yet, and the transcript needs to know whether this was new.
        onExpandAlert?.(alert.id, workItem !== undefined && isWorkItemUnread(workItem));
      }
      setSelectedId(alert.id);
    },
    [isTurnInFlight, showBlockedNotice, markRead, byId, runAction, onExpandAlert, expandedAlertIds]
  );

  const handleDismiss = React.useCallback(
    async (alertId: string) => {
      setSelectedId((current) => (current === alertId ? null : current));
      await archiveWorkItem(alertId);
    },
    [archiveWorkItem]
  );

  if (!isAlertsCenterEnabled) {
    return null;
  }

  const alerts: AssistantAlert[] = needAttention.map(workItemToListedAlert);

  return (
    <>
      <AssistantAlertCarouselBase
        alerts={alerts}
        selectedId={selectedId}
        blockedNoticeAlertId={blockedNoticeAlertId}
        onSelect={handleSelect}
        onDismiss={(alertId) => void handleDismiss(alertId)}
        onViewAll={() => void navigate(PATHS.ALERTS)}
        className={className}
      />
      {alertActionSlot}
    </>
  );
};

export default AssistantAlertCarousel;
